import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import './VideoNode.css';

/**
 * React component for rendering a resizable video inside the editor.
 */
const VideoNodeView = ({ node, updateAttributes, selected }) => {
  const { src, width } = node.attrs;
  const [currentWidth, setCurrentWidth] = useState(width);
  const [isResizing, setIsResizing] = useState(false);
  const videoRef = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  useEffect(() => {
    setCurrentWidth(width);
  }, [width]);

  const handleMouseDown = useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = videoRef.current?.offsetWidth || currentWidth || 480;

    const handleMouseMove = (moveEvent) => {
      const isLeft = corner === 'top-left' || corner === 'bottom-left';
      const diff = moveEvent.clientX - startX.current;
      const delta = isLeft ? -diff : diff;
      const newWidth = Math.max(200, Math.min(startWidth.current + delta, 1200));
      setCurrentWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      const finalWidth = videoRef.current?.offsetWidth || currentWidth;
      updateAttributes({ width: finalWidth });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [currentWidth, updateAttributes]);

  const videoStyle = {
    width: currentWidth ? `${currentWidth}px` : '100%',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '6px',
  };

  return (
    <NodeViewWrapper
      className={`video-node-wrapper ${selected ? 'selected' : ''} ${isResizing ? 'resizing' : ''}`}
      data-drag-handle
    >
      <div className="video-node-inner" style={{ display: 'inline-block', position: 'relative' }}>
        <video
          ref={videoRef}
          src={src}
          style={videoStyle}
          controls
          preload="metadata"
          draggable={false}
          className="video-node-player"
        />

        {selected && (
          <>
            <div
              className="video-resize-handle top-left"
              onMouseDown={(e) => handleMouseDown(e, 'top-left')}
            />
            <div
              className="video-resize-handle top-right"
              onMouseDown={(e) => handleMouseDown(e, 'top-right')}
            />
            <div
              className="video-resize-handle bottom-left"
              onMouseDown={(e) => handleMouseDown(e, 'bottom-left')}
            />
            <div
              className="video-resize-handle bottom-right"
              onMouseDown={(e) => handleMouseDown(e, 'bottom-right')}
            />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

/**
 * Custom TipTap Video node with resize support.
 */
const VideoNode = Node.create({
  name: 'videoNode',

  group: 'block',

  atom: true,

  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      width: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'video[src]',
        getAttrs: (dom) => {
          const width = dom.getAttribute('width') || dom.style.width?.replace('px', '') || null;
          // Check for <source> tag inside <video>
          let src = dom.getAttribute('src');
          if (!src) {
            const source = dom.querySelector('source');
            if (source) src = source.getAttribute('src');
          }
          return {
            src,
            width: width ? parseInt(width, 10) : null,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { width, ...rest } = HTMLAttributes;
    const style = [];

    if (width) {
      style.push(`width: ${width}px`);
      style.push('max-width: 100%');
    } else {
      style.push('max-width: 100%');
    }
    style.push('height: auto');
    style.push('border-radius: 6px');

    return ['video', mergeAttributes(rest, {
      style: style.join('; '),
      controls: true,
      preload: 'metadata',
      width: width || undefined,
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoNodeView);
  },
});

export default VideoNode;
